import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  FileText, Plus, Search, Clock, CheckCircle, XCircle, AlertTriangle, 
  Eye, Send, Trash, Edit, ChevronRight, Filter, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuotedWorksPage = () => {
  const { token, user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [contractors, setContractors] = useState([]);

  const fetchData = async () => {
    try {
      const [quotesRes, dashboardRes, jobsRes, contractorsRes] = await Promise.all([
        fetch(`${API_URL}/api/quoted-works/quotes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/quoted-works/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/ppm-core/jobs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/contractors`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());
      if (contractorsRes.ok) setContractors(await contractorsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      submitted: 'bg-blue-100 text-blue-700',
      under_review: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      revision_requested: 'bg-orange-100 text-orange-700',
      expired: 'bg-gray-100 text-gray-500'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const filteredQuotes = quotes.filter(q => {
    if (filter !== 'all' && q.status !== filter) return false;
    if (searchQuery && !q.quote_number?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !q.title?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleSubmitQuote = async (quoteId) => {
    try {
      const res = await fetch(`${API_URL}/api/quoted-works/quotes/${quoteId}/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Failed to submit quote');
      }
    } catch (error) {
      toast.error('Error submitting quote');
    }
  };

  const handleApproval = async (quoteId, decision, notes) => {
    try {
      const res = await fetch(`${API_URL}/api/quoted-works/quotes/${quoteId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ decision, notes })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowApprovalDialog(false);
        setSelectedQuote(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process approval');
      }
    } catch (error) {
      toast.error('Error processing approval');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Quoted Works Engine</h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">Commercial quote management with approval workflow</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-[#1F7A78] hover:bg-[#005f5f] w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" /> New Quote
        </Button>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          <Card className="cursor-pointer hover:border-gray-300" onClick={() => setFilter('draft')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Draft</p>
                  <p className="text-2xl font-semibold">{dashboard.counts?.draft || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-blue-300" onClick={() => setFilter('submitted')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Approval</p>
                  <p className="text-2xl font-semibold text-blue-600">{dashboard.counts?.pending_approval || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-green-300" onClick={() => setFilter('approved')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-semibold text-green-600">{dashboard.counts?.approved || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-red-300" onClick={() => setFilter('rejected')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-semibold text-red-600">{dashboard.counts?.rejected || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Approved Value</p>
                  <p className="text-2xl font-semibold text-[#1F7A78]">£{(dashboard.values?.approved_total || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search quotes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quotes</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="revision_requested">Revision Requested</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchData} className="shrink-0">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotes Table/Cards */}
      <Card>
        <CardHeader className="pb-2 md:pb-4">
          <CardTitle className="text-lg md:text-xl">Quotes ({filteredQuotes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredQuotes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No quotes found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredQuotes.map(quote => (
                  <div key={quote.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-mono text-sm font-medium">{quote.quote_number}</p>
                        <p className="text-sm text-gray-600 mt-1">{quote.title}</p>
                      </div>
                      {getStatusBadge(quote.status)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <Badge variant="outline" className="capitalize">{quote.approval_level_required}</Badge>
                      <span className="font-semibold text-[#1F7A78]">£{(quote.grand_total || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-gray-500">{new Date(quote.created_at).toLocaleDateString()}</span>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedQuote(quote)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {quote.status === 'draft' && (
                          <Button variant="ghost" size="sm" onClick={() => handleSubmitQuote(quote.id)} className="text-blue-600">
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {['submitted', 'under_review'].includes(quote.status) && 
                         ['admin', 'manager', 'director'].includes(user?.role) && (
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedQuote(quote); setShowApprovalDialog(true); }} className="text-green-600">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Quote #</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Title</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Approval Level</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Total</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Created</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredQuotes.map(quote => (
                      <tr key={quote.id} className="hover:bg-gray-50">
                        <td className="p-3 font-mono text-sm">{quote.quote_number}</td>
                        <td className="p-3">{quote.title}</td>
                        <td className="p-3">{getStatusBadge(quote.status)}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="capitalize">{quote.approval_level_required}</Badge>
                        </td>
                        <td className="p-3 text-right font-semibold">£{(quote.grand_total || 0).toLocaleString()}</td>
                        <td className="p-3 text-sm text-gray-500">
                          {new Date(quote.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setSelectedQuote(quote)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {quote.status === 'draft' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleSubmitQuote(quote.id)}
                                className="text-blue-600"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                            {['submitted', 'under_review'].includes(quote.status) && 
                             ['admin', 'manager', 'director'].includes(user?.role) && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setSelectedQuote(quote); setShowApprovalDialog(true); }}
                                className="text-green-600"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Quote Details Dialog */}
      <Dialog open={!!selectedQuote && !showApprovalDialog} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quote Details - {selectedQuote?.quote_number}</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p>{getStatusBadge(selectedQuote.status)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approval Level</p>
                  <p className="capitalize">{selectedQuote.approval_level_required}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Title</p>
                  <p className="font-medium">{selectedQuote.title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created By</p>
                  <p>{selectedQuote.created_by_name}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">Description</p>
                <p className="text-sm">{selectedQuote.description || 'No description'}</p>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Line Items</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2">Description</th>
                        <th className="text-left p-2">Category</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">Rate</th>
                        <th className="text-right p-2">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(selectedQuote.line_items || []).map((item, idx) => (
                        <tr key={idx}>
                          <td className="p-2">{item.description}</td>
                          <td className="p-2 capitalize">{item.category}</td>
                          <td className="p-2 text-right">{item.quantity}</td>
                          <td className="p-2 text-right">£{item.unit_rate}</td>
                          <td className="p-2 text-right font-medium">£{item.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Labour</span>
                  <span>£{(selectedQuote.labour_total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Materials</span>
                  <span>£{(selectedQuote.materials_total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>£{(selectedQuote.subtotal || 0).toLocaleString()}</span>
                </div>
                {selectedQuote.contingency_amount > 0 && (
                  <div className="flex justify-between">
                    <span>Contingency ({selectedQuote.contingency_percent}%)</span>
                    <span>£{(selectedQuote.contingency_amount || 0).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>VAT ({selectedQuote.vat_rate}%)</span>
                  <span>£{(selectedQuote.vat_amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Grand Total</span>
                  <span className="text-[#1F7A78]">£{(selectedQuote.grand_total || 0).toLocaleString()}</span>
                </div>
              </div>

              {selectedQuote.approval_history?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Approval History</p>
                  <div className="space-y-2">
                    {selectedQuote.approval_history.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <span className="font-medium capitalize">{entry.action?.replace('_', ' ')}</span>
                          <span className="text-gray-500"> by {entry.by}</span>
                          <span className="text-gray-400"> - {new Date(entry.at).toLocaleString()}</span>
                          {entry.notes && <p className="text-gray-600 mt-1">{entry.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve/Reject Quote</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <ApprovalForm 
              quote={selectedQuote}
              onApprove={(decision, notes) => handleApproval(selectedQuote.id, decision, notes)}
              onCancel={() => { setShowApprovalDialog(false); setSelectedQuote(null); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Quote Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Quote</DialogTitle>
          </DialogHeader>
          <CreateQuoteForm 
            jobs={jobs}
            contractors={contractors}
            token={token}
            onSuccess={() => { setShowCreateDialog(false); fetchData(); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Approval Form Component
const ApprovalForm = ({ quote, onApprove, onCancel }) => {
  const [notes, setNotes] = useState('');
  const [decision, setDecision] = useState('');

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="font-medium">{quote.quote_number}</p>
        <p className="text-sm text-gray-500">{quote.title}</p>
        <p className="text-xl font-bold text-[#1F7A78] mt-2">£{(quote.grand_total || 0).toLocaleString()}</p>
        <p className="text-sm text-gray-500">Requires {quote.approval_level_required} approval</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Notes</label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add approval notes..."
          rows={3}
        />
      </div>

      <div className="flex gap-2">
        <Button 
          className="flex-1 bg-green-600 hover:bg-green-700"
          onClick={() => onApprove('approve', notes)}
        >
          <CheckCircle className="w-4 h-4 mr-2" /> Approve
        </Button>
        <Button 
          variant="outline"
          className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50"
          onClick={() => onApprove('request_revision', notes)}
        >
          <Edit className="w-4 h-4 mr-2" /> Request Revision
        </Button>
        <Button 
          variant="outline"
          className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
          onClick={() => onApprove('reject', notes)}
        >
          <XCircle className="w-4 h-4 mr-2" /> Reject
        </Button>
      </div>
    </div>
  );
};

// Create Quote Form Component
const CreateQuoteForm = ({ jobs, contractors, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    job_id: '',
    job_type: 'ppm',
    contractor_id: '',
    title: '',
    description: '',
    line_items: [{ description: '', category: 'labour', quantity: 1, unit: 'each', unit_rate: 0, total: 0 }],
    contingency_percent: 0,
    vat_rate: 20,
    validity_days: 30
  });
  const [submitting, setSubmitting] = useState(false);

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', category: 'labour', quantity: 1, unit: 'each', unit_rate: 0, total: 0 }]
    }));
  };

  const updateLineItem = (idx, field, value) => {
    setFormData(prev => {
      const items = [...prev.line_items];
      items[idx][field] = value;
      if (field === 'quantity' || field === 'unit_rate') {
        items[idx].total = items[idx].quantity * items[idx].unit_rate;
      }
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (idx) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== idx)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.total || 0), 0);
    const contingency = subtotal * (formData.contingency_percent / 100);
    const net = subtotal + contingency;
    const vat = net * (formData.vat_rate / 100);
    return { subtotal, contingency, net, vat, total: net + vat };
  };

  const handleSubmit = async () => {
    if (!formData.job_id || !formData.title) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const totals = calculateTotals();
      const payload = {
        ...formData,
        subtotal: totals.subtotal,
        contingency_amount: totals.contingency,
        vat_amount: totals.vat,
        grand_total: totals.total,
        labour_total: formData.line_items.filter(i => i.category === 'labour').reduce((s, i) => s + i.total, 0),
        materials_total: formData.line_items.filter(i => i.category === 'materials').reduce((s, i) => s + i.total, 0),
        plant_total: formData.line_items.filter(i => i.category === 'plant').reduce((s, i) => s + i.total, 0),
        subcontract_total: formData.line_items.filter(i => i.category === 'subcontract').reduce((s, i) => s + i.total, 0),
        other_total: formData.line_items.filter(i => i.category === 'other').reduce((s, i) => s + i.total, 0)
      };

      const res = await fetch(`${API_URL}/api/quoted-works/quotes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Quote created successfully');
        onSuccess();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create quote');
      }
    } catch (error) {
      toast.error('Error creating quote');
    } finally {
      setSubmitting(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Job *</label>
          <Select value={formData.job_id} onValueChange={(v) => setFormData(prev => ({ ...prev, job_id: v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select job" />
            </SelectTrigger>
            <SelectContent>
              {jobs.map(job => (
                <SelectItem key={job.id} value={job.id}>
                  {job.job_number || job.task_name} - {job.park_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contractor</label>
          <Select value={formData.contractor_id} onValueChange={(v) => setFormData(prev => ({ ...prev, contractor_id: v }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select contractor" />
            </SelectTrigger>
            <SelectContent>
              {contractors.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Title *</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Quote title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Scope of works..."
          rows={2}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium">Line Items</label>
          <Button variant="outline" size="sm" onClick={addLineItem}>
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>
        <div className="space-y-2">
          {formData.line_items.map((item, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-4">
                <Input
                  value={item.description}
                  onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                  placeholder="Description"
                />
              </div>
              <div className="col-span-2">
                <Select value={item.category} onValueChange={(v) => updateLineItem(idx, 'category', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="labour">Labour</SelectItem>
                    <SelectItem value="materials">Materials</SelectItem>
                    <SelectItem value="plant">Plant</SelectItem>
                    <SelectItem value="subcontract">Subcontract</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1">
                <Input
                  type="number"
                  value={item.quantity}
                  onChange={(e) => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                  placeholder="Qty"
                />
              </div>
              <div className="col-span-2">
                <Input
                  type="number"
                  value={item.unit_rate}
                  onChange={(e) => updateLineItem(idx, 'unit_rate', parseFloat(e.target.value) || 0)}
                  placeholder="Rate"
                />
              </div>
              <div className="col-span-2 text-right font-medium">
                £{(item.total || 0).toFixed(2)}
              </div>
              <div className="col-span-1">
                <Button variant="ghost" size="sm" onClick={() => removeLineItem(idx)}>
                  <Trash className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Contingency %</label>
          <Input
            type="number"
            value={formData.contingency_percent}
            onChange={(e) => setFormData(prev => ({ ...prev, contingency_percent: parseFloat(e.target.value) || 0 }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">VAT Rate %</label>
          <Input
            type="number"
            value={formData.vat_rate}
            onChange={(e) => setFormData(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Validity Days</label>
          <Input
            type="number"
            value={formData.validity_days}
            onChange={(e) => setFormData(prev => ({ ...prev, validity_days: parseInt(e.target.value) || 30 }))}
          />
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-1 text-sm">
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>£{totals.subtotal.toFixed(2)}</span>
        </div>
        {formData.contingency_percent > 0 && (
          <div className="flex justify-between">
            <span>Contingency ({formData.contingency_percent}%)</span>
            <span>£{totals.contingency.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>VAT ({formData.vat_rate}%)</span>
          <span>£{totals.vat.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Total</span>
          <span className="text-[#1F7A78]">£{totals.total.toFixed(2)}</span>
        </div>
      </div>

      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#1F7A78] hover:bg-[#005f5f]">
        {submitting ? 'Creating...' : 'Create Quote'}
      </Button>
    </div>
  );
};

export default QuotedWorksPage;
